import {
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  ChangeEvent,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import {
  BoxHeader,
  BoxContent,
  BoxTitle,
  BoxNavRight,
  BoxNavButton,
  BoxNavSearch,
  BoxNavLeft,
  BoxFooter,
} from "./Box";
import { Close } from "./Icons";
import Stack from "./Stack";
import CampaignEditor, {
  EditableCampaign,
  transformCampaignResponse,
} from "./CampaignEditor";
import Button from "./Button";
import {
  CampaignValidationError,
  useSaveCampaign,
} from "./CampaignEditor/util";
import { touchSkipioApi, useSkipioApi } from "../api";
import { useToast } from "../hooks/use-toast";
import { useFields } from "../hooks/use-fields";
import usePreventExit from "../hooks/use-prevent-exit";
import List from "./List";
import Title, { SubTitle } from "./Title";
import { CampaignResponse } from "../api/campaigns";

type SharedProps = {
  onChange: Dispatch<SetStateAction<string | null>>;
  title?: string;
  campaignType?: "drip" | "event" | "reminder";
  clone?: boolean;
  confirm?: boolean;
};

function CampaignSelector({
  trigger,
  ...props
}: SharedProps & {
  trigger: SidePanelTrigger;
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => <CampaignSelectorContent hide={hide} {...props} />}
    />
  );
}

function CampaignSelectorContent({
  onChange = () => {},
  title = "Select a Campaign",
  campaignType = "drip",
  clone = false,
  confirm = true,
  hide,
}: SharedProps & { hide: () => void }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [cloningCampaignId, setCloningCampaignId] = useState<string | null>(
    null
  );

  return (
    <>
      <BoxHeader stacked>
        <BoxNavLeft>
          <BoxTitle>{title}</BoxTitle>
        </BoxNavLeft>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxHeader>
        <BoxNavLeft>
          <BoxNavSearch
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
          />
        </BoxNavLeft>
      </BoxHeader>
      <BoxContent>
        <SidePanel
          trigger={({ show: showCloner }) => (
            <List
              isSelected={(campaign) => selectedId === campaign.id}
              endpoint={({ skipioApi }) => skipioApi.campaigns.get}
              renderTitle={(row) => <Title>{row.name}</Title>}
              renderSubTitle={(row) => (
                <SubTitle>{row.contactsCount} contacts</SubTitle>
              )}
              params={{
                query,
                campaignType,
                sort: "group_name_name",
              }}
              sectionHeaderKey="campaignGroup.name"
              onRowClick={({ rowData: campaign }) => {
                if (clone) {
                  setCloningCampaignId(campaign.id);
                  showCloner();
                } else {
                  if (confirm) setSelectedId(campaign.id);
                  else {
                    onChange(campaign.id);
                    hide();
                  }
                }
              }}
            />
          )}
          content={({ hide: hideCloner }) => (
            <CampaignCloner
              hide={hideCloner}
              campaignId={cloningCampaignId!}
              onChange={(id: string) => {
                onChange(id);
                requestAnimationFrame(() => {
                  hideCloner();
                  hide();
                });
              }}
            />
          )}
        />
      </BoxContent>
      {confirm && (
        <BoxFooter>
          <BoxNavLeft>
            <Button onClick={hide} variant="link">
              Cancel
            </Button>
          </BoxNavLeft>
          <BoxNavRight>
            <Button
              disabled={!selectedId}
              onClick={() => {
                hide();
                onChange(selectedId!);
              }}
            >
              Confirm
            </Button>
          </BoxNavRight>
        </BoxFooter>
      )}
    </>
  );
}

type IdKey = "id" | `${string}Id` | `${string}Ids`;

/** Recursively exclude all "id" object properties */
export type ObjectWithoutIds<T> = T extends (...args: any[]) => any // T is a function
  ? T
  : T extends (infer U)[] // T is an array, recursively remove Ids from each of its elements
  ? ObjectWithoutIds<U>[]
  : T extends object // T is an object, remove Ids from its keys and recursively remove Ids from its values
  ? { [K in Exclude<keyof T, IdKey>]: ObjectWithoutIds<T[K]> }
  : T; // T is a primitive

function removeIds(obj: unknown): unknown {
  if (!obj || typeof obj !== "object" || obj instanceof Date) return obj;
  if (Array.isArray(obj)) return obj.map((item) => removeIds(item));

  return Object.entries(obj)
    .filter(([key]) => key !== "id" && !/Ids?$/.test(key))
    .map(([key, value]) => [key, removeIds(value)])
    .reduce(
      (returned, [key, value]) =>
        Object.assign(returned, { [key as string]: value }),
      {}
    );
}

function CampaignCloner({
  campaignId,
  onChange: newCampaignIdCallback,
  hide,
}: {
  campaignId: string;
  onChange: (newId: string) => void;
  hide: () => void;
}) {
  const saveCampaign = useSaveCampaign();
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const campaign = useMemo(
    () => {
      const campaignResponseWithoutIds = removeIds(
        skipioApi.campaigns[campaignId].get().data as CampaignResponse
      ) as ObjectWithoutIds<CampaignResponse>;
      return transformCampaignResponse(campaignResponseWithoutIds);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignId]
  );

  const [value, onChange] = useState<EditableCampaign>({
    ...campaign,
    contactIds: [],
  });
  const { checkpoint, isChanged } = useFields({
    value,
    onChange,
  });

  const [errors, setErrors] = useState({});

  const showToast = useToast();

  const save = async () => {
    setErrors({});
    try {
      const persistedCampaign = transformCampaignResponse(
        await saveCampaign({ ...value, isCustom: true })
      );
      onChange(persistedCampaign);
      checkpoint(persistedCampaign);

      showToast("Campaign Saved", { success: true });
      touchSkipioApi("campaigns");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      if (persistedCampaign.id) {
        newCampaignIdCallback(persistedCampaign.id);
      }
    } catch (e) {
      if (e instanceof CampaignValidationError) {
        setErrors(e.errors);
        onChange(e.campaign);
      } else throw e;
    }
  };

  usePreventExit(isChanged);

  return (
    <>
      <BoxHeader>
        <BoxTitle>Customize Campaign</BoxTitle>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent>
        <Stack center space={20}>
          <CampaignEditor value={value} onChange={onChange} errors={errors} />
        </Stack>
      </BoxContent>
      <BoxFooter>
        <BoxNavLeft>
          <Button variant="link" onClick={hide}>
            Cancel
          </Button>
        </BoxNavLeft>
        <BoxNavRight>
          <Button onClick={save}>Save</Button>
        </BoxNavRight>
      </BoxFooter>
    </>
  );
}

export default CampaignSelector;
